import Column from '@amzn/meridian/column';
import MeridianModal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import React, { FC, useEffect, useState } from 'react';
import { Button, Checkbox, Text, TranslationFunction } from '../../../components/blocks/localization';
import { DATE_OUTSIDE_WINDOW_TYPE } from '../../../constants';
import { PoReviewRow, ResellerItem } from '../../../redux/types';
import { ExtractionFinding } from '../../../types';
import {
    DELIVERY_DATE_FIELD,
    findingKey,
    findingMessage,
    LineItemsNotice,
    lineItemsNoticeMessage,
    resolutionSentence,
    rowFindingsToRender,
    scannedDocumentNotice,
    SHIP_DATE_FIELD,
} from '../poReviewFindings';
import { EMPTY_CELL, realSku } from './poReviewColumns';

const SHIP_DATE_CONCERN: ExtractionFinding = { type: DATE_OUTSIDE_WINDOW_TYPE, field: SHIP_DATE_FIELD };
const DELIVERY_DATE_CONCERN: ExtractionFinding = { type: DATE_OUTSIDE_WINDOW_TYPE, field: DELIVERY_DATE_FIELD };

export type PoReviewSubmitModalProps = {
    open: boolean;
    flaggedRows: PoReviewRow[];
    unconfirmedPoNumber?: string;
    unconfirmedShipDate?: string;
    unconfirmedDeliveryDate?: string;
    tableNotice: LineItemsNotice;
    // True when the document is a scanned image, so the modal repeats the scanned-document notice.
    isScanned: boolean;
    lineCount: number;
    formattedSubtotal?: string;
    itemMap: Map<string, ResellerItem>;
    onCancel: () => void;
    onSubmit: () => void;
    t: TranslationFunction;
    dataTestId: string;
};

type FlaggedCells = { sku: string; upc: string; asin: string; product: string };

function flaggedCells(row: PoReviewRow, itemMap: Map<string, ResellerItem>): FlaggedCells {
    const item = row.itemId ? itemMap.get(row.itemId) : undefined;
    return {
        sku: (item && realSku(item)) ?? EMPTY_CELL,
        upc: item?.upc ?? EMPTY_CELL,
        asin: item?.asin ?? EMPTY_CELL,
        product: item?.title ?? EMPTY_CELL,
    };
}

const FindingSentences: FC<{ t: TranslationFunction; row: PoReviewRow; itemMap: Map<string, ResellerItem> }> = ({
    t,
    row,
    itemMap,
}) => {
    const resolutionText = resolutionSentence(t, row, itemMap);
    return (
        <Column spacing={'xsmall'}>
            {rowFindingsToRender(row).map((finding) => (
                <Text key={findingKey(finding, row.rowId)} type={'b200'}>
                    {findingMessage(t, finding)}
                </Text>
            ))}
            {resolutionText && (
                <Text key={`${row.rowId}.resolution`} type={'b200'}>
                    {resolutionText}
                </Text>
            )}
        </Column>
    );
};

const FlaggedLineItemsSection: FC<{
    t: TranslationFunction;
    rows: PoReviewRow[];
    itemMap: Map<string, ResellerItem>;
    dataTestId: string;
}> = ({ t, rows, itemMap, dataTestId }) => (
    <Table headerRows={1} showDividers={true} showStripes={true} data-testid={`${dataTestId}.flaggedTable`}>
        <TableRow>
            <TableCell>{t.cognate('#')}</TableCell>
            <TableCell>{t('sku-columnTitle')}</TableCell>
            <TableCell>{t('upc-columnTitle')}</TableCell>
            <TableCell>{t('asin-columnTitle')}</TableCell>
            <TableCell>{t('submitModal-product-columnTitle')}</TableCell>
            <TableCell>{t('quantity-columnTitle')}</TableCell>
            <TableCell>{t('submitModal-findings-columnTitle')}</TableCell>
        </TableRow>
        {rows.map((row) => {
            const cells = flaggedCells(row, itemMap);
            return (
                <TableRow
                    key={row.rowId}
                    highlightOnHover={true}
                    data-testid={`${dataTestId}.flaggedRow[${row.rowId}]`}
                >
                    <TableCell>{String(row.itemNumber)}</TableCell>
                    <TableCell>{cells.sku}</TableCell>
                    <TableCell>{cells.upc}</TableCell>
                    <TableCell>{cells.asin}</TableCell>
                    <TableCell>{cells.product}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>
                        <FindingSentences t={t} row={row} itemMap={itemMap} />
                    </TableCell>
                </TableRow>
            );
        })}
    </Table>
);

export const PoReviewSubmitModal: FC<PoReviewSubmitModalProps> = ({
    open,
    flaggedRows,
    unconfirmedPoNumber,
    unconfirmedShipDate,
    unconfirmedDeliveryDate,
    tableNotice,
    isScanned,
    lineCount,
    formattedSubtotal,
    itemMap,
    onCancel,
    onSubmit,
    t,
    dataTestId,
}) => {
    const [acknowledged, setAcknowledged] = useState(false);
    const describedById = `${dataTestId}.description`;
    const noticeMessage = lineItemsNoticeMessage(t, tableNotice);
    const showsPurchaseOrderDetails = Boolean(unconfirmedPoNumber || unconfirmedShipDate || unconfirmedDeliveryDate);
    const showsLineItems = flaggedRows.length > 0 || Boolean(noticeMessage) || isScanned;

    useEffect(() => {
        if (open) {
            setAcknowledged(false);
        }
    }, [open]);

    return (
        <MeridianModal
            title={t('submitModal-title')}
            open={open}
            onClose={onCancel}
            scrollContainer={'viewport'}
            closeLabel={t('close-text')}
            describedById={describedById}
            bodySpacingInset={'medium'}
            data-testid={dataTestId}
        >
            <div id={describedById}>
                {open && (
                    <Column spacing={'medium'}>
                        {showsPurchaseOrderDetails && (
                            <Column spacing={'small'}>
                                <Text type={'h200'} data-testid={`${dataTestId}.purchaseOrderDetailsHeading`}>
                                    {t('submitModal-purchaseOrderDetails-heading')}
                                </Text>
                                {unconfirmedPoNumber && (
                                    <Text type={'b300'} data-testid={`${dataTestId}.poNumberConcern`}>
                                        {t('submitModal-poNumberUnconfirmed-text', { value: unconfirmedPoNumber })}
                                    </Text>
                                )}
                                {unconfirmedShipDate && (
                                    <Text type={'b300'} data-testid={`${dataTestId}.shipDateConcern`}>
                                        {findingMessage(t, SHIP_DATE_CONCERN, { value: unconfirmedShipDate })}
                                    </Text>
                                )}
                                {unconfirmedDeliveryDate && (
                                    <Text type={'b300'} data-testid={`${dataTestId}.deliveryDateConcern`}>
                                        {findingMessage(t, DELIVERY_DATE_CONCERN, { value: unconfirmedDeliveryDate })}
                                    </Text>
                                )}
                            </Column>
                        )}
                        {showsLineItems && (
                            <Column spacing={'small'}>
                                <Text type={'h200'} data-testid={`${dataTestId}.lineItemsHeading`}>
                                    {t('submitModal-lineItems-heading')}
                                </Text>
                                {noticeMessage && (
                                    <Text type={'b300'} data-testid={`${dataTestId}.tableNotice`}>
                                        {noticeMessage}
                                    </Text>
                                )}
                                {isScanned && (
                                    <Text type={'b300'} data-testid={`${dataTestId}.scannedNotice`}>
                                        {scannedDocumentNotice(t)}
                                    </Text>
                                )}
                                {flaggedRows.length > 0 && (
                                    <Text type={'b300'} data-testid={`${dataTestId}.flaggedIntro`}>
                                        {t('submitModal-flaggedIntro-text')}
                                    </Text>
                                )}
                                {flaggedRows.length > 0 && (
                                    <FlaggedLineItemsSection
                                        t={t}
                                        rows={flaggedRows}
                                        itemMap={itemMap}
                                        dataTestId={dataTestId}
                                    />
                                )}
                            </Column>
                        )}
                        <Text type={'b300'} data-testid={`${dataTestId}.orderSummary`}>
                            {formattedSubtotal ? (
                                <>
                                    {t('submitModal-summaryLead-text', { count: lineCount })}{' '}
                                    <strong>{formattedSubtotal}</strong>{' '}
                                    <small>{t('submitModal-summaryTaxNote-text')}</small>
                                </>
                            ) : (
                                t('submitModal-summaryNoSubtotal-text', { count: lineCount })
                            )}
                        </Text>
                        <Row alignmentHorizontal={'center'}>
                            <Checkbox
                                checked={acknowledged}
                                onChange={setAcknowledged}
                                data-testid={`${dataTestId}.acknowledgeCheckbox`}
                            >
                                {t('submitModal-acknowledge-checkboxLabel')}
                            </Checkbox>
                        </Row>
                    </Column>
                )}
            </div>
            {open && (
                <ModalFooter>
                    <Row alignmentHorizontal={'right'} widths={'fit'}>
                        <Button type={'secondary'} onClick={onCancel} data-testid={`${dataTestId}.cancelButton`}>
                            {t('cancel-buttonLabel')}
                        </Button>
                        <Button
                            type={'primary'}
                            disabled={!acknowledged}
                            onClick={onSubmit}
                            data-testid={`${dataTestId}.submitButton`}
                        >
                            {t('submitModal-confirm-buttonLabel')}
                        </Button>
                    </Row>
                </ModalFooter>
            )}
        </MeridianModal>
    );
};
